import { QUERY_CONFIGURATION_NOT_LOADED, QUERY_CUSTOM_ERROR } from '@common/config';
import { appToast } from '@common/ui/toast';
import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';

const mutex = new Mutex();

export function commonBaseQuery(args: { prefix?: string }) {
  const { prefix } = args;

  return async (args, api, extraOptions) => {
    try {
      const config = (api.getState() as any).configApi.queries['getConfig(undefined)']?.data;

      const backendUrl = config?.BACKEND_URL;
      const apimHeader = config?.apimHeader;
      const apiKey = config?.apimMapKey;
      if (!backendUrl) {
        console.error(QUERY_CONFIGURATION_NOT_LOADED);
        return QUERY_CONFIGURATION_NOT_LOADED;
      }

      const baseQuery = fetchBaseQuery({
        baseUrl: `${backendUrl}${prefix ? `/${prefix}` : ''}`,
        responseHandler: 'content-type',
        credentials: 'include',
        prepareHeaders: (headers) => {
          headers.set(apimHeader, apiKey);
          return headers;
        },
      });

      await mutex.waitForUnlock();

      const httpMethod = (typeof args === 'string') ? 'GET' : (args.method || 'GET').toUpperCase();

      let response: any = await baseQuery(args, api, extraOptions);
      if (!response.error) return response;

      const meta = extractMeta(args);

      if (response.error.status === 'FETCH_ERROR') appToast.error('La connexion avec le serveur a échoué. Veuillez réessayer ultérieurement.');

      if (response.error.status === 401) {
        // checking whether the mutex is locked
        if (!mutex.isLocked()) {
          const release = await mutex.acquire();
          try {
            const refreshResult: any = await baseQuery(
              `${config.authApiUrl}/${config.authApiVersion}/token/refresh`,
              api,
              extraOptions,
            );
            if (refreshResult.data) {
              // retry the initial query
              console.info('Token refreshed !');
              response = await baseQuery(args, api, extraOptions);
            } else {
              console.info('Redirect to LemonLDAP .....');
              window.location.href = `${config.authApiUrl}/${config.authApiOauth2Login}?redirect_uri=${config.authApiOauth2RedirectUrl}`;
            }
          } finally {
            // release must be called once the mutex should be released again.
            release();
          }
        } else {
          // wait until the mutex is available without locking it
          await mutex.waitForUnlock();
          response = await baseQuery(args, api, extraOptions);
        }
      }

      if (meta.skipErrorToast) return response;

      if (response.error.status === 500) appToast.error('Une erreur inattendue s\'est produite.');

      if (httpMethod !== 'GET') {
        if (response.error.status === 403) appToast.warning('Vous n\'êtes pas autorisé(e) à effectuer cette action.');
        if (response.error.status === 400) appToast.warning(`La validation a échouée : ${response.error?.data?.errors?.[0]?.message}`);
        if (response.error.status === 422) appToast.warning(`La validation a échouée : ${response.error?.data?.error}`);
      }

      return response;

    } catch (error: unknown) {
      return QUERY_CUSTOM_ERROR(error);
    }
  };
}

function extractMeta(args: any) {
  return (typeof args === 'object' && 'meta' in args) ? args.meta : {};
}